// frontend/src/components/Layout.js
import React, { useState } from 'react';
import { NavLink, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useActiveDepartment } from '../context/DepartmentContext';
import { Avatar } from './UI';
import Logo, { LogoMark } from './Logo';
import { roleLabel } from '../utils/roleHelpers';
import { visibleModuleGroups } from '../content/modules';
import { NAV_ICONS } from './navIcons';

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  // Department is the single scope control; the program is derived from it.
  const { activeDepartmentId, setActiveDepartmentId, activeProgramId, departments } = useActiveDepartment();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isAdmin    = user?.role === 'admin';
  const isDeptHead = user?.role === 'dept_head';

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const closeMobile = () => setMobileOpen(false);

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* Mobile hamburger */}
      <button className="hamburger-btn" onClick={() => setMobileOpen(o => !o)}>
        {mobileOpen ? '✕' : '☰'}
      </button>

      {/* Mobile overlay */}
      <div className={`sidebar-overlay${mobileOpen ? ' open' : ''}`} onClick={closeMobile} />

      {/* Sidebar */}
      <aside className={`app-sidebar${mobileOpen ? ' open' : ''}`} style={{
        width: collapsed ? 60 : 220, flexShrink: 0, background: 'var(--gray-50)',
        borderRight: '0.5px solid var(--border)', display: 'flex', flexDirection: 'column',
        transition: 'width 0.2s ease', overflow: 'hidden',
      }}>
        {/* Logo — clickable, routes to the dashboard */}
        <div style={{ padding: '18px 14px 14px', borderBottom: '0.5px solid var(--border)' }}>
          {collapsed ? (
            <Link to="/" onClick={closeMobile} aria-label="energin — home" style={{ display: 'inline-flex' }}>
              <LogoMark size={30} />
            </Link>
          ) : (
            <Logo size={21} tagline="Performance" onClick={closeMobile} />
          )}
        </div>

        {/* Department context — the single scope control. Selecting a department
            dictates the program (department → program) and everything downstream.
            Admins/dept heads switch; everyone else is locked to their own. */}
        {!collapsed && (
          <div style={{ padding: '10px 14px', borderBottom: '0.5px solid var(--border)', background: 'var(--surface)' }}>
            <div style={{ fontSize: 9, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 4 }}>
              Department
            </div>
            {(isAdmin || isDeptHead) ? (
              <select
                value={activeDepartmentId || ''}
                onChange={e => setActiveDepartmentId(e.target.value)}
                style={{
                  width: '100%', padding: '6px 8px', fontSize: 12, fontFamily: 'inherit',
                  border: '0.5px solid var(--border-md)', borderRadius: 'var(--radius-md)',
                  background: 'var(--gray-50)', color: 'var(--text-1)', outline: 'none', cursor: 'pointer',
                }}>
                <option value="">All departments</option>
                {departments.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.name}{d.program_name ? ` · ${d.program_name}` : ''}
                  </option>
                ))}
              </select>
            ) : (
              user?.department_name && (
                <div style={{ fontSize: 12, color: 'var(--text-1)', fontWeight: 500, padding: '4px 0' }}>
                  {user.department_name}
                </div>
              )
            )}
          </div>
        )}

        {/* Nav — rendered from content/modules.js. Role AND program filtering
            both live there, so the sidebar cannot advertise a route the guard
            in App.js would refuse. Items with group === null render at the top
            with no heading above them. */}
        <nav style={{ flex: 1, padding: '10px 8px', overflowY: 'auto' }}>
          {visibleModuleGroups(user, activeProgramId).map(({ group, items }) => (
            <div key={group ?? '__ungrouped'}>
              {!collapsed && group && (
                <div style={{ fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.6px', padding: '10px 8px 4px' }}>
                  {group}
                </div>
              )}
              {items.map(item => (
                <NavLink key={item.route} to={item.route} end={item.route === '/'} onClick={closeMobile}
                  title={collapsed ? item.label : undefined}
                  style={({ isActive }) => ({
                    display: 'flex', alignItems: 'center', gap: 9,
                    padding: '8px 10px', borderRadius: 'var(--radius-md)',
                    fontSize: 13, marginBottom: 1,
                    color: isActive ? 'var(--navy-600)' : 'var(--text-2)',
                    background: isActive ? 'var(--navy-50)' : 'transparent',
                    fontWeight: isActive ? 500 : 400,
                    textDecoration: 'none', whiteSpace: 'nowrap', overflow: 'hidden',
                    transition: 'all 0.12s',
                  })}>
                  <span style={{ flexShrink: 0 }}>{NAV_ICONS[item.icon]}</span>
                  {!collapsed && item.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* User card */}
        <div style={{ padding: '10px 10px', borderTop: '0.5px solid var(--border)' }}>
          {!collapsed ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <Link to={`/profile/${user?.id || user?.user_id}`} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 9, flex: 1, minWidth: 0 }}>
                <Avatar initials={user?.avatar_initials || '?'} size={30} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-1)' }}>{user?.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{roleLabel(user?.role)}</div>
                </div>
              </Link>
              <button onClick={handleLogout} title="Log out"
                style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', fontSize: 16, padding: 2 }}>
                ⎋
              </button>
            </div>
          ) : (
            <Link to={`/profile/${user?.id || user?.user_id}`}>
              <Avatar initials={user?.avatar_initials || '?'} size={30} style={{ margin: '0 auto' }} />
            </Link>
          )}
        </div>
      </aside>

      {/* Main */}
      <main className="app-main" style={{ flex: 1, overflowY: 'auto', background: 'var(--gray-50)' }}>
        {children}
      </main>
    </div>
  );
}
